/**
 * Klagenfurt P0.4c/d — advisor return-state store (Web).
 *
 * When the DealAdvisor chat widget opens a comparable listing via
 * internal navigation, we snapshot the advisor state BEFORE
 * navigating away. When the user hits BACK and lands on the SOURCE
 * listing again, the listing page reads this snapshot and:
 *   * automatically re-opens the advisor panel (when it was open),
 *   * loads the SAME conversation via the existing
 *     ``fetchConversationByListing`` query,
 *   * restores the exact chat-container scroll offset (P0.4d Punkt 1)
 *     using — in order of preference — a stable ``anchorTurnUlid``,
 *     an array-position fallback ``anchorTurnIndex``, or the raw
 *     container scroll offset ``chatScrollTop``.
 *
 * There is NO new LLM call — the conversation is server-persisted.
 *
 * The store is a plain sessionStorage wrapper keyed by source ULID so
 * only the intended source page consumes the entry. TTL 30 min so a
 * stale window that was left open for a day never claims a fresh
 * back-navigation.
 *
 * Payload contract (all fields optional except ``savedAt``):
 *   - ``conversationId`` (string|null)
 *       Conversation the panel had loaded when the snapshot was
 *       captured. ``null`` = advisor was not open (modal path).
 *   - ``wasOpen`` (boolean, default true)
 *       When true the listing page should re-open the panel on BACK.
 *       Rental-comparables modal explicitly sets ``false`` so the
 *       host page is NOT force-opened outside the advisor context.
 *   - ``chatScrollTop`` (number|undefined)
 *       Raw ``conversationRef.current.scrollTop`` at snapshot time
 *       (P0.4d Punkt 1). Consumed by AIAdvisorPanel; NOT the
 *       host-page window.scrollY.
 *   - ``anchorTurnUlid`` (string|null)
 *       Stable ID of the topmost visible advisor turn. Preferred
 *       anchor for restore. Falls back to ``anchorTurnIndex`` when
 *       missing (legacy turns don't carry a turn_ulid yet).
 *   - ``anchorTurnIndex`` (number|null)
 *       Positional fallback for the topmost visible turn.
 *   - ``scrollY`` (number)
 *       Host-page ``window.scrollY`` — only consumed on the modal
 *       path (``wasOpen === false``) so a plain rental-comparables
 *       Dialog close does NOT force-open the advisor.
 */

const KEY_PREFIX = "advisor-return:";
const TTL_MS = 30 * 60 * 1000;

export function saveAdvisorReturnState(sourceUlid, state) {
  if (!sourceUlid) return;
  try {
    sessionStorage.setItem(
      KEY_PREFIX + sourceUlid,
      JSON.stringify({
        conversationId: state?.conversationId ?? null,
        wasOpen: state?.wasOpen !== false,
        // P0.4d Punkt 1 — persist the EXACT chat-container scroll
        // top and the topmost turn's stable identifier.
        chatScrollTop:
          typeof state?.chatScrollTop === "number"
            ? state.chatScrollTop
            : undefined,
        anchorTurnUlid:
          typeof state?.anchorTurnUlid === "string" && state.anchorTurnUlid
            ? state.anchorTurnUlid
            : null,
        anchorTurnIndex:
          typeof state?.anchorTurnIndex === "number"
            ? state.anchorTurnIndex
            : null,
        // Kept for the modal-path fallback (wasOpen === false).
        scrollY:
          typeof state?.scrollY === "number"
            ? state.scrollY
            : typeof window !== "undefined"
              ? window.scrollY
              : 0,
        savedAt: Date.now(),
      }),
    );
  } catch {
    // Private mode / quota — non-fatal, just no return state.
  }
}

export function readAdvisorReturnState(sourceUlid) {
  if (!sourceUlid) return null;
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + sourceUlid);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      Date.now() - (parsed.savedAt || 0) > TTL_MS
    ) {
      sessionStorage.removeItem(KEY_PREFIX + sourceUlid);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearAdvisorReturnState(sourceUlid) {
  if (!sourceUlid) return;
  try {
    sessionStorage.removeItem(KEY_PREFIX + sourceUlid);
  } catch {
    /* noop */
  }
}
